<template>
  <div class="map-page modern-theme">
    <Map @map-loaded="onMapLoaded" v-bind="$attrs" ref="map">
      <!-- Hidden BaseMapControl logic -->
      <BaseMapControl ref="basemap" position="top-right" :baseMaps="baseMaps" :control-visible="false"
        :show="activePanels.includes('basemap')" @update:show="val => onPopupShowChange('basemap', val)" />

      <!-- Original LayerControl with nested modules -->
      <LayerControl position="top-left" ref="layerControl" :settings="settings" v-if="!settings.loading" readonly
        :show="activePanels.includes('layer')" :title="titleLayerControl" :control-visible="false"
        @update:layers="l => currentLayers = l" @update:show="val => onPopupShowChange('layer', val)">
        <template #default="{ layers }">
          <AdvancedSearchPanel ref="advancedSearch" :has-btn="false" :layers="layers" position="top-left"
            :control-visible="false" :popup-top="24" :popup-left="492" @click:select="onSelectFeature"
            @results-updated="onSearchResults" @update:loading="l => searchLoading = l" />
          <StatisticControl ref="statistic" :layers="layers" :control-visible="false" position="top-left"
            :popup-top="96" :popup-left="88" :popup-right="null"
            @update:open="val => onPopupShowChange('statistic', val)" />
          <DrawControl position="top-left" ref="drawControl" :control-visible="false" />
        </template>
      </LayerControl>

      <!-- Measurement Control -->
      <MeasurementControl ref="measurement" position="top-left" :control-visible="false"
        @update:drawing-mode="onMeasurementModeChange" />

      <!-- Identify Control -->
      <IdentifyControl ref="identify" :layers="currentLayers" :control-visible="false" position="top-left"
        :popup-top="96" :popup-left="88" :popup-right="null" @click:select="onSelectFeature"
        @update:show="val => onPopupShowChange('identify', val)"
        @update:is-use-click="val => onPopupShowChange('identify', val)" />

      <!-- Legend Popup -->
      <ModuleContainer :prefix="mapId" :control-visible="false" position="top-left">
        <template #draggable="props">
          <DraggablePopup v-bind="props" :show.sync="showLegend" title="Chú giải bản đồ" :width="320" :height="450"
            :top="96" :left="88">
            <div class="pa-2 full-height" style="overflow-y: auto;">
              <LegendContent :legends="displayLegends" />
            </div>
          </DraggablePopup>
        </template>
      </ModuleContainer>

      <!-- Custom V6 Toolbars -->
      <MapShareLeftToolbar ref="leftToolbar" :active-tools="activeTools" @click-layer="toggleTool('layer')"
        @click-legend="toggleTool('legend')" @click-identify="toggleTool('identify')"
        @click-statistic="toggleTool('statistic')" @click-distance="toggleTool('distance')"
        @click-azimuth="toggleTool('azimuth')" @click-area="toggleTool('area')" @click-print="printMap" />

      <MapShareRightToolbar :is-pan-active="!activeMapTool && !isPanLocked" :active-tools="activeTools"
        @click-basemap="toggleTool('basemap')" @click-home="goHome" @click-zoom-in="zoomIn" @click-zoom-out="zoomOut"
        @click-pan="enablePan" @click-refresh="refreshMap" @click-fullscreen="toggleFullscreen" />

      <!-- Global Search Bar (Top Left) -->
      <div class="modern-search-wrapper">
        <MapShareSearch :loading="searchLoading" @search="onBasicSearch"
          @open-filter="$refs.advancedSearch && $refs.advancedSearch.togglePanel()" />
      </div>

      <!-- Tool Instruction Tip -->
      <div v-if="toolInstruction" class="tool-instruction modern-glass-toolbar elevation-4">
        {{ toolInstruction }}
      </div>

      <!-- Bottom Panel for Search Results -->
      <BottomSlidePanel ref="bottomPanel" :show.sync="showBottomPanel" :results="searchResults" :total="searchTotal"
        :loading="searchLoading" :page="searchPage" :limit="searchLimit" :detailLoadingId="detailLoadingId"
        @page-change="onSearchPageChange" @click:info="onInfoClick" @click:export="showListExportDialog = true" />

      <MouseCoordinatesControl v-if="!isMobile" />
      <FeatureInfo ref="featuredetail" />

      <ExportFeatureDialog :show.sync="showListExportDialog" :fields="searchLayerFields" :searchPayload="searchPayload"
        :totalCount="searchTotal" :isMulti="true" />
    </Map>
  </div>
</template>

<script>
import { isString } from 'lodash'
import { callRef } from '@/src/utils/helper'
import { API_BASEMAP, API_LAYER, API_LAYERGROUP, API_SETTING } from '@api'
import sdk from '@/src/service/auth-sdk'
import {
  BaseMapControl,
  Map,
  MouseCoordinatesControl,
  AdvancedSearchPanel,
  MeasurementControl,
  DrawControl,
} from '@components/Map'
import ModuleContainer from '@components/Map/ModuleContainer.vue'
import { BaseMapCreate } from '@models/BaseMap'
import { DraggablePopup } from '@components/draggable'

import FeatureInfo from './layer-control/FeatureInfo.vue'
import LayerControl from './layer-control/layer-control.vue'
import { LAYER_TYPE } from './layer-control/model/layer'
import LegendContent from '@components/Map/control/Legend/LegendContent.vue'
import MapShareSearch from './map-share-search.vue'
import StatisticControl from '@components/Map/control/StatisticControl.vue'
import BottomSlidePanel from '@components/Map/control/BottomSlidePanel.vue'
import ExportFeatureDialog from '@components/search/result/ExportFeatureDialog.vue'
import IdentifyControl from '@components/Map/other-modules/IdentifyControl/IdentifyControl.vue'

import MapShareLeftToolbar from './components/MapShareLeftToolbar.vue'
import MapShareRightToolbar from './components/MapShareRightToolbar.vue'

export default {
  components: {
    Map,
    MouseCoordinatesControl,
    BaseMapControl,
    LayerControl,
    FeatureInfo,
    LegendContent,
    AdvancedSearchPanel,
    MapShareSearch,
    StatisticControl,
    BottomSlidePanel,
    MeasurementControl,
    DrawControl,
    ExportFeatureDialog,
    MapShareLeftToolbar,
    MapShareRightToolbar,
    DraggablePopup,
    ModuleContainer,
    IdentifyControl
  },
  props: {
    titleLayerControl: {
      type: String,
      default: 'Quản lý lớp bản đồ',
    },
    minZoom: {
      type: [Number, String],
      require: false,
    },
    titleMap: {
      type: String,
    },
    legends: {
      type: Array,
      default: () => []
    }
  },
  watch: {
    minZoom(val) {
      this.$refs.map.setMinZoom(val)
    },
    showLegend(val) {
      if (!val && this.activeTool === 'legend') {
        this.activeTool = ''
      }
    },
    activeMapTool(val) {
      switch (val) {
        case 'distance':
        case 'area':
        case 'azimuth':
          this.toolInstruction = 'Nhấp chuột trái để vẽ trên bản đồ. Nhấp chuột phải (hoặc đúp chuột) để kết thúc.';
          break;
        case 'identify':
          this.toolInstruction = 'Nhấp chuột trái vào đối tượng trên bản đồ để xem thông tin chi tiết.';
          break;
        default:
          this.toolInstruction = '';
          break;
      }
    }
  },
  data: () => ({
    activePanels: ['layer'],
    activeMapTool: '',
    isPanLocked: true,
    toolInstruction: '',
    mapId: '',
    currentLayers: [],
    baseMaps: [],
    showLegend: false,
    showBottomPanel: false,
    searchResults: [],
    searchTotal: 0,
    searchLoading: false,
    searchPage: 1,
    searchLimit: 20,
    searchPayload: {},
    searchLayerFields: [],
    showListExportDialog: false,
    detailLoadingId: null,
    settings: {
      loading: false,
      data: {
        is_global_enable: true,
        workspaces_disabled: [],
        workspaces_enabled: [],
      },
    },
  }),
  computed: {
    displayLegends() {
      if (this.legends && this.legends.length > 0) return this.legends;
      // Auto build legends from currentLayers if prop is empty
      let layers = this.currentLayers || [];
      if (!layers || layers.length === 0) return [];
      return layers.map(l => ({
        id: l.id || (l.info ? l.info.id : Math.random()),
        title: l.name || (l.info ? l.info.name : 'Lớp dữ liệu'),
        type: 'list',
        color: (l.info && l.info.color) || '#1976D2'
      }));
    },
    activeTools() {
      return this.activeMapTool ? [...this.activePanels, this.activeMapTool] : [...this.activePanels]
    }
  },
  mounted() {
    API_BASEMAP.list().then((res) => {
      let list = Array.isArray(res) ? res : (res && res.data ? res.data : [])
      if (Array.isArray(list)) {
        this.baseMaps = list.map((x) => BaseMapCreate(x))
      }
    }).catch((err) => {
      console.error('Lỗi lấy danh sách basemap:', err)
    })
    this.$root.$on('layer-control:show-detail', (item) => {
      this.onSelectFeature(item)
    })

    this.checkHasLayerPreview()
    this.checkHasMapPreview()
  },
  methods: {
    // ---- TOOLBAR REMOTE CONTROL & TWO-WAY SYNC ----
    toggleTool(toolId) {
      const panels = ['layer', 'legend', 'basemap'];
      const mapTools = ['distance', 'azimuth', 'area', 'identify', 'statistic'];

      if (panels.includes(toolId)) {
        if (this.activePanels.includes(toolId)) {
          this.activePanels = this.activePanels.filter(p => p !== toolId);
          this.closeTool(toolId);
        } else {
          // PHƯƠNG ÁN 1: Độc quyền hiển thị - Tắt các tool khác
          this.activePanels.forEach(p => this.closeTool(p));
          this.activePanels = [toolId];

          if (this.activeMapTool) {
            this.closeTool(this.activeMapTool);
            this.activeMapTool = '';
          }

          this.openTool(toolId);
        }
      } else if (mapTools.includes(toolId)) {
        if (this.activeMapTool === toolId) {
          this.closeTool(toolId);
          this.activeMapTool = '';
        } else {
          // PHƯƠNG ÁN 1: Độc quyền hiển thị - Tắt các tool khác
          this.activePanels.forEach(p => this.closeTool(p));
          this.activePanels = [];

          if (this.activeMapTool) {
            this.closeTool(this.activeMapTool);
          }
          this.activeMapTool = toolId;
          this.isPanLocked = false;
          this.updatePanInteractions(true); // Disable pan because we are using a map tool
          this.openTool(toolId);
        }
      } else if (toolId === 'print') {
        this.printMap();
      }
    },

    openTool(toolId) {
      if (toolId === 'layer' && this.$refs.layerControl) {
        this.$refs.layerControl.c_show = true
      } else if (toolId === 'legend') {
        this.showLegend = true
      } else if (toolId === 'basemap' && this.$refs.basemap) {
        this.$refs.basemap.show = true
      } else if (toolId === 'identify' && this.$refs.identify) {
        if (!this.$refs.identify.isUseClick) {
          this.$refs.identify.onIdentify()
        }
      } else if (toolId === 'statistic' && this.$refs.statistic) {
        this.$refs.statistic.isOpen = true
      } else if (['distance', 'azimuth', 'area'].includes(toolId) && this.$refs.measurement) {
        if (toolId === 'distance') this.$refs.measurement.onMeasureDistance()
        if (toolId === 'azimuth') this.$refs.measurement.onMeasureAzimuth()
        if (toolId === 'area') this.$refs.measurement.onMeasureArea()
      }
    },

    closeTool(toolId) {
      if (toolId === 'layer' && this.$refs.layerControl) {
        this.$refs.layerControl.c_show = false
      } else if (toolId === 'legend') {
        this.showLegend = false
      } else if (toolId === 'basemap' && this.$refs.basemap) {
        this.$refs.basemap.show = false
      } else if (toolId === 'identify' && this.$refs.identify) {
        if (this.$refs.identify.isUseClick) {
          this.$refs.identify.onIdentify()
        }
      } else if (toolId === 'statistic' && this.$refs.statistic) {
        this.$refs.statistic.isOpen = false
      } else if (['distance', 'azimuth', 'area'].includes(toolId) && this.$refs.measurement) {
        this.$refs.measurement.onClear()
      }
    },

    onPopupShowChange(toolId, isVisible) {
      if (!isVisible) {
        this.activePanels = this.activePanels.filter(p => p !== toolId);
        if (this.activeMapTool === toolId) {
          this.activeMapTool = '';
        }
      } else {
        const panels = ['layer', 'legend', 'basemap'];
        if (panels.includes(toolId) && !this.activePanels.includes(toolId)) {
          this.activePanels.push(toolId);
        }
      }
    },

    onMeasurementModeChange(mode) {
      if (!mode && ['distance', 'azimuth', 'area'].includes(this.activeMapTool)) {
        this.activeMapTool = ''
      }
    },

    // ---- LEFT TOOLBAR ACTIONS ----
    toggleIdentify() {
      if (this.$refs.identify) {
        this.$refs.identify.onIdentify()
      }
    },
    measure(type) {
      if (!this.$refs.measurement) return;
      if (type === 'distance') this.$refs.measurement.onMeasureDistance();
      if (type === 'azimuth') this.$refs.measurement.onMeasureAzimuth();
      if (type === 'area') this.$refs.measurement.onMeasureArea();
    },
    printMap() {
      if (!this.$refs.map || !this.$refs.map.map) return;
      try {
        const map = this.$refs.map.map;
        const dataUrl = map.getCanvas().toDataURL("image/png");
        const a = document.createElement('a');
        a.href = dataUrl;

        // Lấy thông tin bản đồ để đặt tên file
        const zoom = Math.round(map.getZoom());
        const center = map.getCenter();
        const lng = center.lng.toFixed(4);
        const lat = center.lat.toFixed(4);
        const timestamp = new Date().getTime();

        a.download = `bando_z${zoom}_${lng}_${lat}_${timestamp}.png`;

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        this.$store.dispatch('notify/success', { message: "Xuất ảnh bản đồ thành công!" });
      } catch (err) {
        console.error("Lỗi khi xuất ảnh bản đồ:", err);
        this.$store.dispatch('notify/error', { message: "Không thể xuất ảnh bản đồ." });
      }
    },

    // ---- RIGHT TOOLBAR ACTIONS ----
    goHome() {
      if (this.$refs.map && this.$refs.map.map) {
        // Default Vietnam center
        this.$refs.map.map.flyTo({ center: [105.8, 16.0], zoom: 5 });
      }
    },
    zoomIn() {
      if (this.$refs.map && this.$refs.map.map) this.$refs.map.map.zoomIn();
    },
    zoomOut() {
      if (this.$refs.map && this.$refs.map.map) this.$refs.map.map.zoomOut();
    },
    updatePanInteractions(isLocked, mapInstance = null) {
      const map = mapInstance || (this.$refs.map && this.$refs.map.map);
      if (map) {
        if (isLocked) {
          if (map.dragPan) map.dragPan.disable();
          // DO NOT disable scrollZoom, doubleClickZoom, touchZoomRotate so users can still zoom
        } else {
          if (map.dragPan) map.dragPan.enable();
        }
      }
    },
    enablePan() {
      // 1. Nếu đang mở tool tương tác bản đồ (VD: Đo đạc), thì tắt tool đó và mở khóa Pan
      if (this.activeMapTool) {
        this.closeTool(this.activeMapTool);
        this.activeMapTool = '';
        this.isPanLocked = false;
      } else {
        // 2. Nếu không có tool nào mở, toggle khóa bản đồ
        this.isPanLocked = !this.isPanLocked;
      }

      // 3. Xóa các đối tượng vẽ, đo đạc, xem thông tin nếu còn sót lại
      if (this.$refs.measurement) this.$refs.measurement.onClear();
      if (this.$refs.drawControl) this.$refs.drawControl.clearDraw();
      if (this.$refs.identify) this.$refs.identify.onClose();

      // 4. Trả con trỏ chuột về mặc định
      if (this.$refs.map && this.$refs.map.map) {
        const map = this.$refs.map.map;
        const canvas = map.getCanvas();
        canvas.classList.remove('pointer');
        canvas.classList.remove('cursor-crosshair');
        canvas.style.cursor = '';
      }

      // Khóa/Mở khóa cuối cùng để đảm bảo các component khác không ghi đè
      this.$nextTick(() => {
        this.updatePanInteractions(this.isPanLocked);
      });
    },
    refreshMap() {
      window.location.reload();
    },
    toggleFullscreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        }
      }
    },

    // ---- OTHER METHODS ----
    async checkHasLayerPreview() {
      let layer_ids = this.$route.query.layer_ids
      if (!layer_ids) {
        return
      }
      layer_ids = convertParamsToArray(layer_ids)
      let res = await API_LAYER.list({
        ids: layer_ids,
        with: 'workspace,style,datastoreDetail.fields',
      })
      let layers = res.data
      if (!layers || layers.length < 1) {
        return
      }
      callRef(
        () => {
          this.$refs.layerControl.onAddLayer({
            layers: layers.map((x) => ({
              layer_type: x.layer_type,
              data_type: x.data_type,
              info: x,
            })),
          })
        },
        () => this.$refs.layerControl
      )
    },
    async checkHasMapPreview() {
      let map_ids = this.$route.query.map_ids
      if (!map_ids) {
        return
      }
      map_ids = convertParamsToArray(map_ids)
      let res = await API_LAYERGROUP.list({ ids: map_ids, with: 'workspace' })
      let layerGroups = res.data
      if (!layerGroups || layerGroups.length < 1) {
        return
      }
      callRef(
        () => {
          this.$refs.layerControl.onAddLayerGroup({ layerGroups })
        },
        () => this.$refs.layerControl
      )
    },
    onSelectFeature(item) {
      if (item)
        callRef(
          () => this.$refs.featuredetail.open(item),
          () => this.$refs.featuredetail
        )
    },
    async onInfoClick(item) {
      if (!item) return
      this.detailLoadingId = item.id
      try {
        let layer_id = item.layer_id
        if (!layer_id && this.$refs.advancedSearch) {
          layer_id = this.$refs.advancedSearch.searchForm.layer_id
        }
        let res = await sdk.request({
          url: 'map/detail',
          method: 'post',
          data: {
            data_type: 'nbds_vector_layer',
            data_id: layer_id,
            feature_id: item.id,
          },
        })
        this.onSelectFeature({ ...res.data, feature: item })
        if (this.$refs.bottomPanel) {
          this.$refs.bottomPanel.collapse()
        }
      } catch (error) {
        console.error("Lỗi lấy chi tiết:", error)
      } finally {
        this.detailLoadingId = null
      }
    },
    onBasicSearch(keyword) {
      callRef(() => {
        this.$refs.advancedSearch.searchForm.keyword = keyword
        this.$refs.advancedSearch.doSearch(1)
      }, () => this.$refs.advancedSearch)
    },
    onSearchResults({ results, total, page, limit, searchPayload, layerFields }) {
      this.searchResults = results
      this.searchTotal = total
      this.searchPage = page
      this.searchLimit = limit
      this.searchPayload = searchPayload
      this.searchLayerFields = layerFields || []
      this.showBottomPanel = true
    },
    onSearchPageChange(page) {
      callRef(() => {
        this.$refs.advancedSearch.doSearch(page)
      }, () => this.$refs.advancedSearch)
    },
    getDataSave() {
      let layers = this.$refs.layerControl.getLayers()
      return { layers }
    },
    async setDataSave({ layers = [] } = {}, settings = {}) {
      let layer_ids = layers
        .filter((x) => x.layer_type == LAYER_TYPE.NBDS_VECTO_LAYER)
        .map((x) => x.info.id)
      let data_group = layers.reduce((acc, cur) => {
        if (cur.group) {
          acc[cur.info.id] = { group: cur.group }
        }
        return acc
      }, {})
      let res = await Promise.all([
        API_LAYER.list({
          ids: layer_ids,
          with: 'workspace,style,datastoreDetail.fields',
        }).then((res) => {
          let layers_data = res.data
          if (!layers_data || layers_data.length < 1) {
            return []
          }
          layers_data = layers_data.reduce((acc, cur) => {
            acc[cur.id] = cur
            return acc
          }, {})
          return layers.map((x) => ({
            layer_type: LAYER_TYPE.NBDS_VECTO_LAYER,
            info: layers_data[x.info.id],
            group: (data_group[x.info.id] || {}).group,
          }))
        }),
      ])
      let layer_convert = res.flat()
      callRef(
        () => {
          this.$refs.layerControl.onAddLayer({
            layers: layer_convert,
          })

          if (settings) {
            if (settings.default_open_label) {
              this.$refs.layerControl.showAllLabel(true)
            }
          }
        },
        () => this.$refs.layerControl
      )
    },
    onMapLoaded(map) {
      if (map && map.id) {
        this.mapId = map.id
      }
      setTimeout(() => {
        this.updatePanInteractions(this.isPanLocked, map);
      }, 100);
      map.on('moveend', (e) => {
        this.$emit('get-position-map', {
          center: map.getCenter(),
          zoom: map.getZoom(),
        })
      })
    },
  },
}
function convertParamsToArray(value, separator = ',') {
  if (isString(value)) {
    value = value.split(separator)
  }
  return value
}
</script>

<style src="./map-share-modern.scss" lang="scss"></style>
<style scoped>
.map-page {
  position: relative;
  width: 100%;
  height: 100%;
}

.map-page>div {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
}
</style>
